import { isExampleResource } from '@gonfalon/analytics/isExampleResource';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import type { DraftExperimentFormProps, Experiment } from '@gonfalon/experiments/types';
import { msg } from '@gonfalon/messages/msg';
import { toastQueue } from '@launchpad-ui/components';

import { useUpdateExperimentWithFields } from '#components/experimentation/common/hooks/useUpdateExperimentWithFields';
import { useUpdateLayerForDraftExperiment } from '#components/experimentation/common/hooks/useUpdateLayerForDraftExperiment';
import { useUpdateStratifiedSamplingForDraftExperiment } from '#components/experimentation/common/hooks/useUpdateStratifiedSamplingForDraftExperiment';
import { invalidateExperimentQueries } from '#components/experimentation/common/utils/invalidateExperimentQueries';
import { trackExperimentEvent, trackIterationStarted, trackMABEvent } from '#utils/analyticsUtils';

type UpdateDraftExperimentProps = {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
  defaultValues: DraftExperimentFormProps;
  experiment?: Experiment;
};

export type UpdateDraftExperimentOptions = {
  /** When true, includes startIteration instruction in the same API call (for running experiments) */
  saveAndStart?: boolean;
  /** Reason/justification for the change (used with saveAndStart) */
  changeJustification?: string;
  /** Callback to invoke on successful completion (only used with saveAndStart) */
  onSuccess?: () => void;
};

export const useDraftExperimentActionCallbacks = (props: UpdateDraftExperimentProps) => {
  const { updateExperimentWithFields } = useUpdateExperimentWithFields(props);

  const { updateStratifiedSamplingAfterIteration } = useUpdateStratifiedSamplingForDraftExperiment(props);
  const { updateLayerBeforeIteration, revertLayerUpdate } = useUpdateLayerForDraftExperiment(props);

  const revertLayerUpdateOnRejection = async (
    updatedDraftExperimentProps: DraftExperimentFormProps,
    error: unknown,
  ) => {
    if (!isRESTAPIError(error) || error.status >= 500) {
      return;
    }

    try {
      await revertLayerUpdate(updatedDraftExperimentProps, props.defaultValues);
    } catch {
      const isMAB = props.experiment?.type === 'mab';
      toastQueue.add({
        description: msg('experiments.experimentFormToast.layerRevertFailed'),
        status: 'error',
      });
      (isMAB ? trackMABEvent : trackExperimentEvent)('Layer revert after failed experiment update failed', {
        experimentKey: props.experimentKey,
        projectKey: props.projectKey,
        environmentKey: props.environmentKey,
        layerKey: updatedDraftExperimentProps.layerConfig?.key,
      });
    }
  };

  const updateDraftExperimentWithIteration = async (
    updatedDraftExperimentProps: DraftExperimentFormProps,
    options?: UpdateDraftExperimentOptions,
  ) => {
    const layerUpdated = await updateLayerBeforeIteration(updatedDraftExperimentProps, props.defaultValues);

    let updatedExperiment: Experiment | undefined;
    try {
      // Single updateExperimentFields instruction handles both experiment and iteration updates.
      // For running experiments with saveAndStart, this sends saveAndStartIteration in one call.
      updatedExperiment = await updateExperimentWithFields(updatedDraftExperimentProps, {
        saveAndStart: options?.saveAndStart,
        changeJustification: options?.changeJustification,
      });
    } catch (error) {
      if (layerUpdated) {
        await revertLayerUpdateOnRejection(updatedDraftExperimentProps, error);
      }
      throw error;
    }

    await updateStratifiedSamplingAfterIteration(updatedDraftExperimentProps, props.defaultValues);

    // Invalidate queries to ensure UI reflects new iteration
    const shouldInvalidate =
      options?.saveAndStart || ['not_started', 'stopped'].includes(props.experiment?.currentIteration?.status ?? '');

    if (shouldInvalidate) {
      await invalidateExperimentQueries({
        projectKey: props.projectKey,
        environmentKey: props.environmentKey,
        experimentKey: props.experimentKey,
      });
    }

    const isMAB = props.experiment?.type === 'mab';
    const includesStart = options?.saveAndStart;

    // Call onSuccess callback if provided (used for saveAndStart to trigger UI updates)
    if (includesStart && options?.onSuccess) {
      options.onSuccess();
    }

    (isMAB ? trackMABEvent : trackExperimentEvent)(
      includesStart
        ? `${isMAB ? 'Multi-armed Bandit' : 'Experiment'} save and start iteration`
        : `${isMAB ? 'Multi-armed Bandit' : 'Experiment'} updated with Fields`,
      {
        experimentKey: props.experimentKey,
        projectKey: props.projectKey,
        environmentKey: props.environmentKey,
        experimentStatus: props.experiment?.currentIteration?.status,
        isExampleResource: isExampleResource(props.experimentKey),
        ...(includesStart
          ? {}
          : {
              defaultExperimentValues: props.defaultValues,
              updatedExperimentValues: updatedDraftExperimentProps,
            }),
      },
    );

    if (includesStart) {
      trackIterationStarted({
        experimentKey: props.experimentKey,
        projectKey: props.projectKey,
        environmentKey: props.environmentKey,
        experimentType: props.experiment?.type,
        source: 'save_and_start',
        iteration: updatedExperiment?.currentIteration,
      });
    }

    return updatedExperiment;
  };

  return {
    updateDraftExperimentWithIteration,
  };
};
